import { useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import type { TooltipContentProps } from 'recharts';
import type {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';
import { useI18nContext } from '@/i18n/i18n-react';
import type { TenantRepurchaseRateChartResponseDto } from '@/generated/korza-api-client/types.gen';
import { type ChartGranularity } from '@/lib/chart-axis';
import { chartColor } from '@/lib/chart-colors';
import { useMappedChartData } from '@/lib/hooks/use-chart-data';
import { usePartialBuckets } from '@/lib/hooks/use-partial-buckets';
import { partialReadoutLabel, type ChartRange } from '@/lib/partial-buckets';
import { MetricsBarChart } from '@/components/ui/charts/bar-chart';
import { MetricsChartBarSeries } from '@/components/ui/charts/chart-bar-series';

type TenantRepurchaseRateChartPointDto =
  TenantRepurchaseRateChartResponseDto['points'][number];

export type Granularity = ChartGranularity;

interface RepurchaseRateChartProps {
  granularity: Granularity;
  points: TenantRepurchaseRateChartPointDto[];
  /** The filter's period: hatches the partial first/last bucket (CHART-01). */
  range?: ChartRange;
  currency?: string;
  countAxisMax?: number;
  syncId?: string;
  syncMethod?: 'index' | 'value';
  /**
   * When provided, the whole period column is clickable (any series, any
   * height); `bucketStart` is the point's ISO date.
   */
  onBucketClick?: (bucketStart: string) => void;
}

export function RepurchaseRateChart({
  granularity,
  points,
  range,
  currency = 'USD',
  countAxisMax,
  syncId,
  syncMethod,
  onBucketClick,
}: RepurchaseRateChartProps) {
  const { LL, locale } = useI18nContext();

  const rows = useMappedChartData(points, granularity, locale, (p) => ({
    date: p.date,
    inAppRepurchaseQuantity: p.inAppRepurchaseQuantity,
    channelMigrationQuantity: p.channelMigrationQuantity,
    totalRepurchaseQuantity: p.totalRepurchaseQuantity,
    totalRepurchaseRevenue: p.totalRepurchaseRevenue,
    inAppRepurchaseRevenue: p.inAppRepurchaseRevenue,
    channelMigrationRevenue: p.channelMigrationRevenue,
  }));
  const partial = usePartialBuckets(rows, range, granularity);
  const chartData = useMemo(
    () =>
      rows.map((row, index) => ({
        ...row,
        partial: partial.indexes.includes(index),
      })),
    [rows, partial.indexes]
  );

  const inAppLabel = LL.metricsPage.repurchases.inAppRepurchase();
  const channelLabel = LL.metricsPage.repurchases.channelMigration();

  const handleBucketClick = onBucketClick
    ? (row: Record<string, unknown>) => {
        if (typeof row.date === 'string') onBucketClick(row.date);
      }
    : undefined;

  const tooltipContent = ({
    active,
    payload,
    label,
  }: TooltipContentProps<ValueType, NameType>) => {
    if (!active || !payload?.length) return null;

    const row = payload[0]?.payload as (typeof chartData)[number] | undefined;
    if (!row) return null;

    return (
      <div className="rounded-lg border border-input bg-background p-2 shadow-sm">
        <p className="text-sm text-muted-foreground">
          {partialReadoutLabel(String(label ?? ''), row.partial, LL)}
        </p>
        <p className="text-sm" style={{ color: chartColor(1) }}>
          {inAppLabel}:{' '}
          {new Intl.NumberFormat(locale, { style: 'decimal' }).format(
            row.inAppRepurchaseQuantity
          )}{' '}
          <span className="text-muted-foreground">
            (
            {new Intl.NumberFormat(locale, {
              style: 'currency',
              currency,
            }).format(row.inAppRepurchaseRevenue)}
            )
          </span>
        </p>
        <p className="text-sm" style={{ color: chartColor(2) }}>
          {channelLabel}:{' '}
          {new Intl.NumberFormat(locale, { style: 'decimal' }).format(
            row.channelMigrationQuantity
          )}{' '}
          <span className="text-muted-foreground">
            (
            {new Intl.NumberFormat(locale, {
              style: 'currency',
              currency,
            }).format(row.channelMigrationRevenue)}
            )
          </span>
        </p>
        <p className="mt-1 border-t border-border pt-1 text-sm text-foreground">
          {LL.metricsPage.repurchases.title()}:{' '}
          {new Intl.NumberFormat(locale, { style: 'decimal' }).format(
            row.totalRepurchaseQuantity
          )}
        </p>
        <p className="text-sm text-green-500">
          {new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
          }).format(row.totalRepurchaseRevenue)}
        </p>
        {onBucketClick ? (
          <p className="mt-1.5 flex items-center gap-1 border-t border-border pt-1.5 text-xs font-medium text-primary">
            {LL.metricsPage.repurchaseDrilldown.chartClickHint()}
            <ArrowRight className="size-3" aria-hidden="true" />
          </p>
        ) : null}
      </div>
    );
  };

  return (
    <MetricsBarChart
      data={chartData}
      partialLegend={partial.edges}
      syncId={syncId}
      syncMethod={syncMethod}
      yAxisDomain={
        countAxisMax !== undefined
          ? [0, countAxisMax]
          : [0, (dataMax: number) => Math.max(1, Math.ceil(dataMax) + 1)]
      }
      tooltipContent={tooltipContent}
      onBucketClick={handleBucketClick}
    >
      <MetricsChartBarSeries
        dataKey="inAppRepurchaseQuantity"
        name={inAppLabel}
        stackId="repurchases"
        colorKey={2}
        radius={[0, 0, 0, 0]}
        partialIndexes={partial.indexes}
      />
      <MetricsChartBarSeries
        dataKey="channelMigrationQuantity"
        name={channelLabel}
        stackId="repurchases"
        colorKey={1}
        radius={[2, 2, 0, 0]}
        partialIndexes={partial.indexes}
        partialLabels={partial.labels}
      />
    </MetricsBarChart>
  );
}
